/*************************************************************
 * ConfigurationsTab.jsx - Enhanced Application settings and preferences tab
 * CUSTOMER DEPLOYMENT VERSION - Enhanced Alert System with Global Check Settings
 *************************************************************/

import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext.jsx';
import { api } from '../api/reportingApi.js';
import { LoadingSpinner } from '../components/ui/LoadingSpinner.jsx';

export const ConfigurationsTab = () => {
    const { state, addNotification, updatePreferences } = useAppContext();
    const [localPreferences, setLocalPreferences] = useState(state.preferences);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingPrefs, setLoadingPrefs] = useState(true);
    const [activeSubTab, setActiveSubTab] = useState('general');
    
    // Customer deployment settings - behavior section disabled with fixed values
    const CUSTOMER_DEPLOYMENT_MODE = true; // Set to false to re-enable behavior section
    const FIXED_BEHAVIOR_SETTINGS = {
        autoRefresh: false,
        refreshInterval: 30,
        show_confidence_scores: true,
        includeManualImages: true
    };
    
    // Notification settings state
    const [notificationSettings, setNotificationSettings] = useState({
        email_enabled: false,
        sms_enabled: false,
        app_enabled: true,
        light_tower_enabled: false,
        email: '',
        phone: '',
        defect_notifications: true,
        station_offline_notifications: true,
        session_completed_notifications: false,
        daily_summary_enabled: false
    });
    
    // Global alert check frequency settings
    const [alertCheckSettings, setAlertCheckSettings] = useState({
        inspection_check_enabled: true,
        inspection_interval: 3, // Check every 3 inspections
        time_check_enabled: true,
        time_interval: 5 // Check every 5 minutes
    });

    // Enhanced alert rules state with all new alert types (without individual check frequency)
    const [alertRules, setAlertRules] = useState([
        {
            id: 1,
            name: 'High Defect Rate Alert',
            type: 'defect_rate',
            threshold: 10,
            enabled: true,
            conditions: { 
                timeframe: '1h', 
                min_samples: 10
            }
        },
        {
            id: 2,
            name: 'Station Offline Alert',
            type: 'station_offline',
            threshold: 5,
            enabled: true,
            conditions: { 
                timeframe: '5m'
            }
        },
        {
            id: 3,
            name: 'Low Model Confidence',
            type: 'low_confidence',
            threshold: 70,
            enabled: true,
            conditions: { 
                timeframe: '30m', 
                min_samples: 5
            }
        }
    ]);

    // Alert type definitions with enhanced metadata (removed check frequency options)
    const ALERT_TYPES = {
        'defect_rate': {
            label: 'Defect Rate Threshold',
            description: 'Triggers when defect rate exceeds threshold over specified period',
            thresholdUnit: '%',
            supportsTimeframe: true,
            supportsMinSamples: true
        },
        'station_offline': {
            label: 'Station Offline Alert',
            description: 'Triggers when station goes offline during working hours',
            thresholdUnit: 'minutes',
            supportsTimeframe: true,
            supportsMinSamples: false
        },
        'consecutive_defects': {
            label: 'Consecutive Defects',
            description: 'Triggers after specified number of consecutive defects',
            thresholdUnit: 'defects',
            supportsTimeframe: false,
            supportsMinSamples: false
        },
        's500_malfunction': {
            label: 'S500 System Malfunction',
            description: 'Triggers on camera, PLC, or software crashes',
            thresholdUnit: 'incidents',
            supportsTimeframe: true,
            supportsMinSamples: false
        },
        'defect_type_spike': {
            label: 'Specific Defect Type Spike',
            description: 'Triggers when specific defect type exceeds normal levels',
            thresholdUnit: '%',
            supportsTimeframe: true,
            supportsMinSamples: true
        },
        'general_defect_spike': {
            label: 'General Defect Spike',
            description: 'Triggers when overall defect rate spikes suddenly',
            thresholdUnit: '%',
            supportsTimeframe: true,
            supportsMinSamples: true
        },
        'confidence_drop': {
            label: 'Detection Confidence Drop',
            description: 'Triggers when detection confidence drops suddenly',
            thresholdUnit: '%',
            supportsTimeframe: true,
            supportsMinSamples: true
        }
    };

    useEffect(() => {
        const loadAllSettings = async () => {
            try {
                setLoadingPrefs(true);
                
                console.log('Loading settings - Customer deployment mode:', CUSTOMER_DEPLOYMENT_MODE);
                
                // Load preferences
                const prefsResponse = await api.getPreferences();
                if (prefsResponse && prefsResponse.success && prefsResponse.data) {
                    const prefs = prefsResponse.data;
                    const mappedPrefs = {
                        theme: prefs.theme || 'light',
                        itemsPerPage: prefs.itemsPerPage || 20,
                        default_date_range: prefs.default_date_range || 'week',
                        workingHoursStart: prefs.workingHoursStart || '08:00',
                        workingHoursEnd: prefs.workingHoursEnd || '18:00',
                        workingDays: prefs.workingDays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
                        // Apply fixed behavior settings for customer deployment
                        ...(CUSTOMER_DEPLOYMENT_MODE ? FIXED_BEHAVIOR_SETTINGS : {
                            autoRefresh: prefs.autoRefresh || false,
                            refreshInterval: prefs.refreshInterval || 30,
                            show_confidence_scores: prefs.show_confidence_scores !== false,
                            includeManualImages: prefs.includeManualImages || false
                        })
                    };
                    
                    console.log('Applied preferences:', mappedPrefs);
                    console.log('Fixed behavior settings applied:', CUSTOMER_DEPLOYMENT_MODE ? 'YES' : 'NO');
                    
                    setLocalPreferences(mappedPrefs);
                    updatePreferences(mappedPrefs);
                }
                
                // Load notification settings
                try {
                    const notifResponse = await api.getNotificationSettings();
                    if (notifResponse && notifResponse.success) {
                        setNotificationSettings(prev => ({ ...prev, ...notifResponse.data }));
                    }
                } catch (error) {
                    console.warn('Notification settings not available:', error);
                }
                
                // Load alert check settings
                try {
                    const alertCheckResponse = await api.getAlertCheckSettings();
                    if (alertCheckResponse && alertCheckResponse.success) {
                        setAlertCheckSettings(prev => ({ ...prev, ...alertCheckResponse.data }));
                    }
                } catch (error) {
                    console.warn('Alert check settings not available:', error);
                }
                
                // Load alert rules
                try {
                    const alertResponse = await api.getAlertRules();
                    if (alertResponse && alertResponse.success) {
                        setAlertRules(alertResponse.data || alertRules);
                    }
                } catch (error) {
                    console.warn('Alert rules not available:', error);
                }
                
            } catch (error) {
                console.error('Error loading settings:', error);
                addNotification({
                    type: 'warning',
                    message: 'Could not load some settings, using defaults',
                    duration: 3000
                });
            } finally {
                setLoadingPrefs(false);
            }
        };

        loadAllSettings();
    }, []);

    const handlePreferenceChange = (key, value) => {
        // Prevent changes to behavior settings in customer deployment mode
        if (CUSTOMER_DEPLOYMENT_MODE && Object.keys(FIXED_BEHAVIOR_SETTINGS).includes(key)) {
            console.warn(`Attempt to change behavior setting "${key}" blocked in customer deployment mode`);
            addNotification({
                type: 'info',
                message: 'Behavior settings are currently locked for this deployment',
                duration: 2000
            });
            return;
        }
        
        console.log(`Preference change: ${key} = ${value}`);
        setLocalPreferences(prev => ({ ...prev, [key]: value }));
    };

    const handleNotificationChange = (key, value) => {
        setNotificationSettings(prev => ({ ...prev, [key]: value }));
    };

    const handleAlertCheckChange = (key, value) => {
        setAlertCheckSettings(prev => ({ ...prev, [key]: value }));
    };

    const savePreferences = async () => {
        try {
            setIsLoading(true);
            
            console.log('Saving preferences:', localPreferences);
            
            // Apply theme immediately
            if (localPreferences.theme !== 'system') {
                window.applyTheme?.(localPreferences.theme);
            } else {
                window.applyTheme?.('system');
            }

            // Save to backend
            await api.savePreferences(localPreferences);
            
            // Update local state
            updatePreferences(localPreferences);

            addNotification({
                type: 'success',
                message: 'Preferences saved successfully',
                duration: 3000
            });
        } catch (error) {
            console.error('Error saving preferences:', error);
            addNotification({
                type: 'danger',
                message: 'Failed to save preferences: ' + error.message,
                duration: 5000
            });
        } finally {
            setIsLoading(false);
        }
    };

    const saveNotificationSettings = async () => {
        try {
            setIsLoading(true);
            await api.saveNotificationSettings(notificationSettings);
            
            addNotification({
                type: 'success',
                message: 'Notification settings saved successfully',
                duration: 3000
            });
        } catch (error) {
            console.error('Error saving notification settings:', error);
            addNotification({
                type: 'danger',
                message: 'Failed to save notification settings: ' + error.message,
                duration: 5000
            });
        } finally {
            setIsLoading(false);
        }
    };

    const saveAlertCheckSettings = async () => {
        try {
            setIsLoading(true);
            await api.saveAlertCheckSettings(alertCheckSettings);
            
            addNotification({
                type: 'success',
                message: 'Alert check settings saved successfully',
                duration: 3000
            });
        } catch (error) {
            console.error('Error saving alert check settings:', error);
            addNotification({
                type: 'danger',
                message: 'Failed to save alert check settings: ' + error.message,
                duration: 5000
            });
        } finally {
            setIsLoading(false);
        }
    };

    const saveAlertRules = async () => {
        try {
            setIsLoading(true);
            
            // Validate alert rules before saving
            const invalidRules = alertRules.filter(rule => {
                if (!rule.name || rule.name.trim() === '') return true;
                if (!rule.type || !ALERT_TYPES[rule.type]) return true;
                if (rule.threshold <= 0) return true;
                return false;
            });

            if (invalidRules.length > 0) {
                addNotification({
                    type: 'warning',
                    message: `${invalidRules.length} alert rule(s) have validation errors. Please check and fix them.`,
                    duration: 5000
                });
                setIsLoading(false);
                return;
            }

            await api.saveAlertRules(alertRules);
            
            addNotification({
                type: 'success',
                message: 'Alert rules saved successfully',
                duration: 3000
            });
        } catch (error) {
            console.error('Error saving alert rules:', error);
            addNotification({
                type: 'danger',
                message: 'Failed to save alert rules: ' + error.message,
                duration: 5000
            });
        } finally {
            setIsLoading(false);
        }
    };

    const addAlertRule = () => {
        const newRule = {
            id: Date.now(),
            name: 'New Alert Rule',
            type: 'defect_rate',
            threshold: 5,
            enabled: true,
            conditions: { 
                timeframe: '1h', 
                min_samples: 10,
                defect_type: null // For defect_type_spike alerts
            }
        };
        setAlertRules(prev => [...prev, newRule]);
    };

    const updateAlertRule = (id, updates) => {
        setAlertRules(prev => prev.map(rule => {
            if (rule.id === id) {
                const updatedRule = { ...rule, ...updates };
                
                // Update conditions based on rule type
                if (updates.type && updates.type !== rule.type) {
                    const alertType = ALERT_TYPES[updates.type];
                    updatedRule.conditions = {
                        ...updatedRule.conditions,
                        timeframe: alertType.supportsTimeframe ? '1h' : undefined,
                        min_samples: alertType.supportsMinSamples ? 10 : undefined
                    };
                }
                
                return updatedRule;
            }
            return rule;
        }));
    };

    const removeAlertRule = (id) => {
        setAlertRules(prev => prev.filter(rule => rule.id !== id));
    };

    const renderGeneralSettings = () => (
        <div className="row">
            <div className="col-md-6">
                <h6 className="font-weight-bold mb-3">Appearance</h6>
                <div className="form-group">
                    <label className="form-label">Theme</label>
                    <select
                        className="form-control"
                        value={localPreferences.theme || 'light'}
                        onChange={(e) => handlePreferenceChange('theme', e.target.value)}
                    >
                        <option value="light">Light</option>
                        <option value="dark">Dark</option>
                        <option value="system">System Default</option>
                    </select>
                </div>
                <div className="form-group">
                    <label className="form-label">Items Per Page</label>
                    <select
                        className="form-control"
                        value={localPreferences.itemsPerPage || 20}
                        onChange={(e) => handlePreferenceChange('itemsPerPage', parseInt(e.target.value))}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
                <div className="form-group">
                    <label className="form-label">Default Date Range</label>
                    <select
                        className="form-control"
                        value={localPreferences.default_date_range || 'week'}
                        onChange={(e) => handlePreferenceChange('default_date_range', e.target.value)}
                    >
                        <option value="today">Today</option>
                        <option value="week">Last 7 Days</option>
                        <option value="month">Last 30 Days</option>
                        <option value="year">Last Year</option>
                    </select>
                </div>
            </div>
            <div className="col-md-6">
                {/* Behavior section - conditionally rendered based on deployment mode */}
                {CUSTOMER_DEPLOYMENT_MODE ? (
                    <div>
                        <h6 className="font-weight-bold mb-3">
                            Behavior 
                            <span className="badge badge-info ml-2">Locked for Customer Deployment</span>
                        </h6>
                        <div className="alert alert-info">
                            <h6 className="alert-heading">
                                <i className="fas fa-lock mr-2"></i>
                                Current Behavior Settings (Read-Only)
                            </h6>
                            <hr />
                            <div className="row">
                                <div className="col-12">
                                    <ul className="list-unstyled mb-0">
                                        <li className="mb-2">
                                            <i className="fas fa-sync-alt mr-2 text-muted"></i>
                                            <strong>Auto Refresh:</strong> 
                                            <span className={`badge ml-2 ${FIXED_BEHAVIOR_SETTINGS.autoRefresh ? 'badge-success' : 'badge-secondary'}`}>
                                                {FIXED_BEHAVIOR_SETTINGS.autoRefresh ? 'Enabled' : 'Disabled'}
                                            </span>
                                        </li>
                                        <li className="mb-2">
                                            <i className="fas fa-percentage mr-2 text-muted"></i>
                                            <strong>Show Confidence Scores:</strong> 
                                            <span className={`badge ml-2 ${FIXED_BEHAVIOR_SETTINGS.show_confidence_scores ? 'badge-success' : 'badge-secondary'}`}>
                                                {FIXED_BEHAVIOR_SETTINGS.show_confidence_scores ? 'Enabled' : 'Disabled'}
                                            </span>
                                        </li>
                                        <li className="mb-0">
                                            <i className="fas fa-images mr-2 text-muted"></i>
                                            <strong>Include Manual Images:</strong> 
                                            <span className={`badge ml-2 ${FIXED_BEHAVIOR_SETTINGS.includeManualImages ? 'badge-success' : 'badge-secondary'}`}>
                                                {FIXED_BEHAVIOR_SETTINGS.includeManualImages ? 'Enabled' : 'Disabled'}
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <hr />
                            <small className="text-muted">
                                <i className="fas fa-info-circle mr-1"></i>
                                These settings are managed centrally for this customer deployment. 
                                Contact your system administrator if changes are needed.
                            </small>
                        </div>
                    </div>
                ) : (
                    <div>
                        <h6 className="font-weight-bold mb-3">Behavior</h6>
                        <div className="form-group">
                            <div className="custom-control custom-switch">
                                <input
                                    type="checkbox"
                                    className="custom-control-input"
                                    id="autoRefresh"
                                    checked={localPreferences.autoRefresh || false}
                                    onChange={(e) => handlePreferenceChange('autoRefresh', e.target.checked)}
                                />
                                <label className="custom-control-label" htmlFor="autoRefresh">
                                    Auto Refresh Data
                                </label>
                            </div>
                        </div>
                        {localPreferences.autoRefresh && (
                            <div className="form-group">
                                <label className="form-label">Refresh Interval (seconds)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    min="10"
                                    max="300"
                                    value={localPreferences.refreshInterval || 30}
                                    onChange={(e) => handlePreferenceChange('refreshInterval', parseInt(e.target.value))}
                                />
                            </div>
                        )}
                        <div className="form-group">
                            <div className="custom-control custom-switch">
                                <input
                                    type="checkbox"
                                    className="custom-control-input"
                                    id="showConfidence"
                                    checked={localPreferences.show_confidence_scores !== false}
                                    onChange={(e) => handlePreferenceChange('show_confidence_scores', e.target.checked)}
                                />
                                <label className="custom-control-label" htmlFor="showConfidence">
                                    Show Confidence Scores
                                </label>
                            </div>
                        </div>
                        <div className="form-group">
                            <div className="custom-control custom-switch">
                                <input
                                    type="checkbox"
                                    className="custom-control-input"
                                    id="includeManualImages"
                                    checked={localPreferences.includeManualImages || false}
                                    onChange={(e) => handlePreferenceChange('includeManualImages', e.target.checked)}
                                />
                                <label className="custom-control-label" htmlFor="includeManualImages">
                                    Include Manual Images in Reports
                                </label>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="col-12">
                <h6 className="font-weight-bold mb-3 mt-4">Working Hours</h6>
                <div className="row">
                    <div className="col-md-3">
                        <label className="form-label">Start Time</label>
                        <input
                            type="time"
                            className="form-control"
                            value={localPreferences.workingHoursStart || '08:00'}
                            onChange={(e) => handlePreferenceChange('workingHoursStart', e.target.value)}
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label">End Time</label>
                        <input
                            type="time"
                            className="form-control"
                            value={localPreferences.workingHoursEnd || '18:00'}
                            onChange={(e) => handlePreferenceChange('workingHoursEnd', e.target.value)}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Working Days</label>
                        <div className="d-flex flex-wrap">
                            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                                <div key={day} className="custom-control custom-checkbox mr-3">
                                    <input
                                        type="checkbox"
                                        className="custom-control-input"
                                        id={`workDay${day}`}
                                        checked={(localPreferences.workingDays || []).includes(day)}
                                        onChange={(e) => {
                                            const days = localPreferences.workingDays || [];
                                            if (e.target.checked) {
                                                handlePreferenceChange('workingDays', [...days, day]);
                                            } else {
                                                handlePreferenceChange('workingDays', days.filter(d => d !== day));
                                            }
                                        }}
                                    />
                                    <label className="custom-control-label" htmlFor={`workDay${day}`}>
                                        {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderNotificationSettings = () => (
        <div className="row">
            <div className="col-md-6">
                <div className="card mb-4">
                    <div className="card-header">
                        <h5>Notification Methods</h5>
                        <small className="text-muted">Choose how you want to receive notifications</small>
                    </div>
                    <div className="card-body">
                        <div className="form-group">
                            <div className="custom-control custom-switch">
                                <input 
                                    type="checkbox" 
                                    className="custom-control-input" 
                                    id="appNotifications"
                                    checked={notificationSettings.app_enabled}
                                    onChange={(e) => handleNotificationChange('app_enabled', e.target.checked)}
                                />
                                <label className="custom-control-label" htmlFor="appNotifications">In-App Notifications</label>
                            </div>
                            <small className="form-text text-muted">Receive notifications within the application interface</small>
                        </div>
                        
                        <div className="form-group mt-3">
                            <div className="custom-control custom-switch">
                                <input 
                                    type="checkbox" 
                                    className="custom-control-input" 
                                    id="emailNotifications"
                                    checked={notificationSettings.email_enabled}
                                    onChange={(e) => handleNotificationChange('email_enabled', e.target.checked)}
                                />
                                <label className="custom-control-label" htmlFor="emailNotifications">Email Notifications</label>
                            </div>
                            <input 
                                type="email" 
                                className="form-control mt-2" 
                                placeholder="Email address"
                                value={notificationSettings.email}
                                onChange={(e) => handleNotificationChange('email', e.target.value)}
                                disabled={!notificationSettings.email_enabled}
                            />
                        </div>
                        
                        <div className="form-group mt-3">
                            <div className="custom-control custom-switch">
                                <input 
                                    type="checkbox" 
                                    className="custom-control-input" 
                                    id="smsNotifications"
                                    checked={notificationSettings.sms_enabled}
                                    onChange={(e) => handleNotificationChange('sms_enabled', e.target.checked)}
                                />
                                <label className="custom-control-label" htmlFor="smsNotifications">SMS Notifications</label>
                            </div>
                            <input 
                                type="tel" 
                                className="form-control mt-2" 
                                placeholder="Phone number"
                                value={notificationSettings.phone}
                                onChange={(e) => handleNotificationChange('phone', e.target.value)}
                                disabled={!notificationSettings.sms_enabled}
                            />
                        </div>
                        
                        <div className="form-group mt-3">
                            <div className="custom-control custom-switch">
                                <input 
                                    type="checkbox" 
                                    className="custom-control-input" 
                                    id="lightTowerNotifications"
                                    checked={notificationSettings.light_tower_enabled}
                                    onChange={(e) => handleNotificationChange('light_tower_enabled', e.target.checked)}
                                />
                                <label className="custom-control-label" htmlFor="lightTowerNotifications">Light Tower Alerts</label>
                            </div>
                            <small className="form-text text-muted">Enable visual alerts on connected light towers</small>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* <div className="col-md-6">
                <div className="card mb-4">
                    <div className="card-header">
                        <h5>Notification Events</h5>
                        <small className="text-muted">Select which events trigger notifications</small>
                    </div>
                    <div className="card-body">
                        <div className="form-group">
                            <div className="custom-control custom-switch">
                                <input 
                                    type="checkbox" 
                                    className="custom-control-input" 
                                    id="defectNotifications"
                                    checked={notificationSettings.defect_notifications}
                                    onChange={(e) => handleNotificationChange('defect_notifications', e.target.checked)}
                                />
                                <label className="custom-control-label" htmlFor="defectNotifications">Defect Alerts</label>
                            </div>
                            <small className="form-text text-muted">Receive notifications when defects are detected</small>
                        </div>
                        
                        <div className="form-group mt-3">
                            <div className="custom-control custom-switch">
                                <input 
                                    type="checkbox" 
                                    className="custom-control-input" 
                                    id="stationOfflineNotifications"
                                    checked={notificationSettings.station_offline_notifications}
                                    onChange={(e) => handleNotificationChange('station_offline_notifications', e.target.checked)}
                                />
                                <label className="custom-control-label" htmlFor="stationOfflineNotifications">Station Offline Alerts</label>
                            </div>
                            <small className="form-text text-muted">Receive notifications when stations go offline during working hours</small>
                        </div>
                        
                        <div className="form-group mt-3">
                            <div className="custom-control custom-switch">
                                <input 
                                    type="checkbox" 
                                    className="custom-control-input" 
                                    id="sessionCompletedNotifications"
                                    checked={notificationSettings.session_completed_notifications}
                                    onChange={(e) => handleNotificationChange('session_completed_notifications', e.target.checked)}
                                />
                                <label className="custom-control-label" htmlFor="sessionCompletedNotifications">Session Completed Alerts</label>
                            </div>
                            <small className="form-text text-muted">Receive notifications when inspection sessions are completed</small>
                        </div>
                        
                        <div className="form-group mt-3">
                            <div className="custom-control custom-switch">
                                <input 
                                    type="checkbox" 
                                    className="custom-control-input" 
                                    id="dailySummaryEnabled"
                                    checked={notificationSettings.daily_summary_enabled}
                                    onChange={(e) => handleNotificationChange('daily_summary_enabled', e.target.checked)}
                                />
                                <label className="custom-control-label" htmlFor="dailySummaryEnabled">Daily Summary Reports</label>
                            </div>
                            <small className="form-text text-muted">Receive a daily summary of all activity</small>
                        </div>
                    </div>
                </div>
            </div> */}
        </div>
    );

    const renderAlertRules = () => (
        <div>
            {/* Individual Alert Rules */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h6 className="mb-0">Individual Alert Rules</h6>
                    <small className="text-muted">Configure specific conditions that trigger alerts</small>
                </div>
                <button 
                    className="btn btn-primary btn-sm"
                    onClick={addAlertRule}
                >
                    <i className="fas fa-plus mr-1"></i>
                    Add Rule
                </button>
            </div>
            
            {alertRules.map(rule => {
                const alertType = ALERT_TYPES[rule.type];
                return (
                    <div key={rule.id} className="card mb-3">
                        <div className="card-header">
                            <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">{rule.name}</h6>
                                <div className="d-flex align-items-center">
                                    <div className="custom-control custom-switch mr-3">
                                        <input
                                            type="checkbox"
                                            className="custom-control-input"
                                            id={`enabled${rule.id}`}
                                            checked={rule.enabled}
                                            onChange={(e) => updateAlertRule(rule.id, { enabled: e.target.checked })}
                                        />
                                        <label className="custom-control-label" htmlFor={`enabled${rule.id}`}>
                                            Active
                                        </label>
                                    </div>
                                    <button
                                        className="btn btn-danger btn-sm"
                                        onClick={() => removeAlertRule(rule.id)}
                                        title="Delete rule"
                                    >
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                {/* Basic Rule Settings */}
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label className="form-label">Rule Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={rule.name}
                                            onChange={(e) => updateAlertRule(rule.id, { name: e.target.value })}
                                            placeholder="Enter rule name"
                                        />
                                    </div>
                                </div>
                                
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label className="form-label">Alert Type</label>
                                        <select
                                            className="form-control"
                                            value={rule.type}
                                            onChange={(e) => updateAlertRule(rule.id, { type: e.target.value })}
                                        >
                                            {Object.entries(ALERT_TYPES).map(([key, type]) => (
                                                <option key={key} value={key}>{type.label}</option>
                                            ))}
                                        </select>
                                        <small className="form-text text-muted">{alertType?.description}</small>
                                    </div>
                                </div>
                                
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label className="form-label">
                                            Threshold {alertType?.thresholdUnit && `(${alertType.thresholdUnit})`}
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={rule.threshold}
                                            onChange={(e) => updateAlertRule(rule.id, { threshold: parseFloat(e.target.value) || 0 })}
                                            min="0"
                                            step={rule.type.includes('rate') || rule.type.includes('confidence') ? "0.1" : "1"}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Advanced Conditions - Simplified without check frequency */}
                            <div className="row">
                                {/* Timeframe for applicable rule types */}
                                {alertType?.supportsTimeframe && (
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="form-label">Analysis Timeframe</label>
                                            <select
                                                className="form-control"
                                                value={rule.conditions?.timeframe || '1h'}
                                                onChange={(e) => updateAlertRule(rule.id, { 
                                                    conditions: { ...rule.conditions, timeframe: e.target.value }
                                                })}
                                            >
                                                <option value="5m">5 minutes</option>
                                                <option value="15m">15 minutes</option>
                                                <option value="30m">30 minutes</option>
                                                <option value="1h">1 hour</option>
                                                <option value="4h">4 hours</option>
                                                <option value="8h">8 hours</option>
                                                <option value="24h">24 hours</option>
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {/* Minimum samples for applicable rule types */}
                                {alertType?.supportsMinSamples && (
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="form-label">Min. Samples</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={rule.conditions?.min_samples || 10}
                                                onChange={(e) => updateAlertRule(rule.id, { 
                                                    conditions: { 
                                                        ...rule.conditions, 
                                                        min_samples: parseInt(e.target.value) || 1 
                                                    }
                                                })}
                                                min="1"
                                            />
                                            <small className="form-text text-muted">Minimum data points required</small>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Special fields for specific alert types */}
                            {rule.type === 'defect_type_spike' && (
                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="form-label">Specific Defect Type</label>
                                            <select
                                                className="form-control"
                                                value={rule.conditions?.defect_type || ''}
                                                onChange={(e) => updateAlertRule(rule.id, { 
                                                    conditions: { ...rule.conditions, defect_type: e.target.value }
                                                })}
                                            >
                                                <option value="">Select defect type...</option>
                                                <option value="scratch">Scratch</option>
                                                <option value="dent">Dent</option>
                                                <option value="crack">Crack</option>
                                                <option value="discoloration">Discoloration</option>
                                                <option value="missing_component">Missing Component</option>
                                                <option value="wrong_component">Wrong Component</option>
                                                <option value="dimension_error">Dimension Error</option>
                                                <option value="surface_defect">Surface Defect</option>
                                                <option value="assembly_error">Assembly Error</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {rule.type === 's500_malfunction' && (
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="alert alert-info">
                                            <h6 className="alert-heading">S500 Malfunction Detection</h6>
                                            <p className="mb-0">This alert monitors for:</p>
                                            <ul className="mb-0 mt-2">
                                                <li>Camera connection failures</li>
                                                <li>PLC communication errors</li>
                                                <li>Software crashes or exceptions</li>
                                                <li>System resource exhaustion</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Rule Summary - Updated without check frequency */}
                            <div className="row mt-3">
                                <div className="col-12">
                                    <div className="alert alert-light">
                                        <h6 className="alert-heading">Rule Summary</h6>
                                        <p className="mb-0">
                                            <strong>{rule.name}</strong> will trigger when{' '}
                                            {rule.type === 'defect_rate' && `defect rate exceeds ${rule.threshold}%`}
                                            {rule.type === 'station_offline' && `station is offline for more than ${rule.threshold} minutes during working hours`}
                                            {rule.type === 'consecutive_defects' && `${rule.threshold} consecutive defects are detected`}
                                            {rule.type === 's500_malfunction' && `S500 system malfunctions occur more than ${rule.threshold} times`}
                                            {rule.type === 'defect_type_spike' && `${rule.conditions?.defect_type || 'selected defect type'} exceeds ${rule.threshold}% of inspections`}
                                            {rule.type === 'general_defect_spike' && `overall defect rate spikes above ${rule.threshold}%`}
                                            {rule.type === 'confidence_drop' && `detection confidence drops below ${rule.threshold}%`}
                                            {alertType?.supportsTimeframe && ` over ${rule.conditions?.timeframe || '1h'}`}
                                            {alertType?.supportsMinSamples && 
                                                ` (minimum ${rule.conditions?.min_samples || 10} samples required)`}.
                                            <br />
                                            <small className="text-muted">
                                                <strong>Check frequency:</strong> Based on global settings - 
                                                {alertCheckSettings.inspection_check_enabled && ` every ${alertCheckSettings.inspection_interval} inspection(s)`}
                                                {alertCheckSettings.inspection_check_enabled && alertCheckSettings.time_check_enabled && ' and'}
                                                {alertCheckSettings.time_check_enabled && ` every ${alertCheckSettings.time_interval} minute(s)`}
                                            </small>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}

            {alertRules.length === 0 && (
                <div className="text-center py-5">
                    <i className="fas fa-exclamation-triangle fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Alert Rules Configured</h5>
                    <p className="text-muted">Click "Add Rule" to create your first alert rule.</p>
                </div>
            )}

            {/* Global Alert Check Settings -- MOVED HERE */}
            <div className="card mb-4 mt-5">
                <div className="card-header">
                    <h6 className="mb-0">
                        <i className="fas fa-clock mr-2"></i>
                        Global Alert Check Settings
                    </h6>
                    <small className="text-muted">Configure how frequently the system checks for alert conditions</small>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <div className="custom-control custom-switch">
                                    <input
                                        type="checkbox"
                                        className="custom-control-input"
                                        id="inspectionCheckEnabled"
                                        checked={alertCheckSettings.inspection_check_enabled}
                                        onChange={(e) => handleAlertCheckChange('inspection_check_enabled', e.target.checked)}
                                    />
                                    <label className="custom-control-label" htmlFor="inspectionCheckEnabled">
                                        Check Every X Inspections
                                    </label>
                                </div>
                                {alertCheckSettings.inspection_check_enabled && (
                                    <div className="mt-2">
                                        <label className="form-label">Check Every</label>
                                        <div className="input-group">
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={alertCheckSettings.inspection_interval}
                                                onChange={(e) => handleAlertCheckChange('inspection_interval', parseInt(e.target.value) || 1)}
                                                min="1"
                                                max="100"
                                            />
                                            <div className="input-group-append">
                                                <span className="input-group-text">inspections</span>
                                            </div>
                                        </div>
                                        <small className="form-text text-muted">
                                            Alert rules will be evaluated every {alertCheckSettings.inspection_interval} inspection{alertCheckSettings.inspection_interval !== 1 ? 's' : ''}
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="col-md-6">
                            <div className="form-group">
                                <div className="custom-control custom-switch">
                                    <input
                                        type="checkbox"
                                        className="custom-control-input"
                                        id="timeCheckEnabled"
                                        checked={alertCheckSettings.time_check_enabled}
                                        onChange={(e) => handleAlertCheckChange('time_check_enabled', e.target.checked)}
                                    />
                                    <label className="custom-control-label" htmlFor="timeCheckEnabled">
                                        Check Every X Minutes
                                    </label>
                                </div>
                                {alertCheckSettings.time_check_enabled && (
                                    <div className="mt-2">
                                        <label className="form-label">Check Every</label>
                                        <div className="input-group">
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={alertCheckSettings.time_interval}
                                                onChange={(e) => handleAlertCheckChange('time_interval', parseInt(e.target.value) || 1)}
                                                min="1"
                                                max="1440"
                                            />
                                            <div className="input-group-append">
                                                <span className="input-group-text">minutes</span>
                                            </div>
                                        </div>
                                        <small className="form-text text-muted">
                                            Alert rules will be evaluated every {alertCheckSettings.time_interval} minute{alertCheckSettings.time_interval !== 1 ? 's' : ''}
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <div className="alert alert-info mt-3">
                        <h6 className="alert-heading">
                            <i className="fas fa-info-circle mr-2"></i>
                            How Alert Checking Works
                        </h6>
                        <ul className="mb-0">
                            <li><strong>Inspection-based:</strong> Alerts are checked after every N completed inspections</li>
                            <li><strong>Time-based:</strong> Alerts are checked periodically every N minutes (useful for offline detection)</li>
                            <li><strong>Both can be enabled:</strong> The system will use whichever condition triggers first</li>
                            <li><strong>Rule-specific conditions:</strong> Each rule still has its own analysis timeframe and sample requirements</li>
                        </ul>
                    </div>
                    
                    <div className="mt-3">
                        <button
                            className={`btn btn-primary ${isLoading ? 'disabled' : ''}`}
                            onClick={saveAlertCheckSettings}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <span>
                                    <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                                    Saving...
                                </span>
                            ) : (
                                <span>
                                    <i className="fas fa-save mr-2"></i>
                                    Save Check Settings
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    if (loadingPrefs) {
        return (
            <LoadingSpinner
                message="Loading configuration settings..."
                size="normal"
            />
        );
    }

    return (
        <div className="settings-content fade-in">
            <div className="card shadow-sm">
                <div className="card-header">
                    <h5 className="mb-0">
                        <i className="fas fa-cog text-primary mr-2"></i>
                        Analytics Configuration
                        {CUSTOMER_DEPLOYMENT_MODE && (
                            <span className="badge badge-info ml-2">Customer Deployment Mode</span>
                        )}
                    </h5>
                    <p className="text-muted mb-0 mt-1">Manage application settings, notifications, and comprehensive alert rules</p>
                </div>
                <div className="card-body">
                    {/* Sub-tab navigation */}
                    <ul className="nav nav-pills mb-4" role="tablist">
                        <li className="nav-item">
                            <button 
                                className={`nav-link ${activeSubTab === 'general' ? 'active' : ''}`}
                                onClick={() => setActiveSubTab('general')}
                            >
                                <i className="fas fa-cogs mr-1"></i>
                                General
                            </button>
                        </li>
                        <li className="nav-item">
                            <button 
                                className={`nav-link ${activeSubTab === 'notifications' ? 'active' : ''}`}
                                onClick={() => setActiveSubTab('notifications')}
                            >
                                <i className="fas fa-bell mr-1"></i>
                                Notifications
                            </button>
                        </li>
                        <li className="nav-item">
                            <button 
                                className={`nav-link ${activeSubTab === 'alerts' ? 'active' : ''}`}
                                onClick={() => setActiveSubTab('alerts')}
                            >
                                <i className="fas fa-exclamation-triangle mr-1"></i>
                                Alert Rules
                                <span className="badge badge-secondary ml-1">{alertRules.length}</span>
                            </button>
                        </li>
                    </ul>

                    {/* Tab content */}
                    <div className="tab-content">
                        {activeSubTab === 'general' && renderGeneralSettings()}
                        {activeSubTab === 'notifications' && renderNotificationSettings()}
                        {activeSubTab === 'alerts' && renderAlertRules()}
                    </div>

                    <hr className="my-4" />
                    
                    {/* Save buttons and debug info */}
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            {/* <h6 className="mb-1">Debug Information</h6>
                            <small className="text-muted d-block">
                                ES6 Modular React implementation - Enhanced Alert System v2.0
                            </small>
                            <div className="mt-2">
                                <small className="text-muted d-block">
                                    Alert Types: {Object.keys(ALERT_TYPES).length}
                                </small>
                                <small className="text-muted d-block">
                                    Active Rules: {alertRules.filter(r => r.enabled).length}/{alertRules.length}
                                </small>
                                <small className="text-muted d-block">
                                    API Requests: {api.getStats().requestCount}
                                </small>
                                <small className="text-muted d-block">
                                    React Version: {React.version || 'Unknown'}
                                </small>
                            </div> */}
                        </div>
                        <div className="btn-group">
                            {activeSubTab === 'general' && (
                                <button
                                    className={`btn btn-primary ${isLoading ? 'disabled' : ''}`}
                                    onClick={savePreferences}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <span>
                                            <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                                            Saving...
                                        </span>
                                    ) : (
                                        <span>
                                            <i className="fas fa-save mr-2"></i>
                                            Save Preferences
                                        </span>
                                    )}
                                </button>
                            )}
                            
                            {activeSubTab === 'notifications' && (
                                <button
                                    className={`btn btn-primary ${isLoading ? 'disabled' : ''}`}
                                    onClick={saveNotificationSettings}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <span>
                                            <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                                            Saving...
                                        </span>
                                    ) : (
                                        <span>
                                            <i className="fas fa-save mr-2"></i>
                                            Save Notifications
                                        </span>
                                    )}
                                </button>
                            )}
                            
                            {activeSubTab === 'alerts' && (
                                <div className="btn-group">
                                    <button
                                        className={`btn btn-secondary ${isLoading ? 'disabled' : ''}`}
                                        onClick={saveAlertCheckSettings}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <span>
                                                <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                                                Saving...
                                            </span>
                                        ) : (
                                            <span>
                                                <i className="fas fa-clock mr-2"></i>
                                                Save Check Settings
                                            </span>
                                        )}
                                    </button>
                                    <button
                                        className={`btn btn-primary ${isLoading ? 'disabled' : ''}`}
                                        onClick={saveAlertRules}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <span>
                                                <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                                                Saving...
                                            </span>
                                        ) : (
                                            <span>
                                                <i className="fas fa-save mr-2"></i>
                                                Save Alert Rules
                                            </span>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
